import { useState, useEffect } from 'react';
import {
  UserCircleIcon,
  KeyIcon,
  BellIcon,
  ShieldCheckIcon,
  PaintBrushIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Header } from '@/components/Layout';
import { useAuthStore } from '@/stores/auth';
import { authApi } from '@/services/api';
import Modal from '@/components/common/Modal';
import { LoadingSpinner } from '@/components/common';
import TwoFactorSetup from '@/components/TwoFactorSetup';
import TwoFactorDisable from '@/components/TwoFactorDisable';
import NotificationPreferencesEditor from '@/components/cms/NotificationPreferencesEditor';

type SettingsTab = 'profile' | 'security' | 'notifications' | 'appearance';

const tabs = [
  { id: 'profile' as const, name: 'Perfil', icon: UserCircleIcon },
  { id: 'security' as const, name: 'Seguridad', icon: ShieldCheckIcon },
  { id: 'notifications' as const, name: 'Notificaciones', icon: BellIcon },
  { id: 'appearance' as const, name: 'Apariencia', icon: PaintBrushIcon },
];

interface TwoFactorStatus {
  enabled: boolean;
  enabled_at?: string;
  backup_codes_remaining: number;
}

export default function SettingsPage() {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [loading, setLoading] = useState(false);

  // Password change form
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: '',
  });

  // 2FA state
  const [twoFactorStatus, setTwoFactorStatus] = useState<TwoFactorStatus | null>(null);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);

  // Regenerate backup codes
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenerateCode, setRegenerateCode] = useState('');
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [newBackupCodes, setNewBackupCodes] = useState<string[] | null>(null);
  const [copiedCodes, setCopiedCodes] = useState(false);

  useEffect(() => {
    if (activeTab === 'security') {
      load2faStatus();
    }
  }, [activeTab]);

  const load2faStatus = async () => {
    setTwoFactorLoading(true);
    try {
      const response = await authApi.get2faStatus();
      setTwoFactorStatus(response.data);
    } catch {
      // Silently fail - user might not have 2FA configured
      setTwoFactorStatus({ enabled: false, backup_codes_remaining: 0 });
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwords.new !== passwords.confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (passwords.new.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    setLoading(true);
    try {
      await authApi.changePassword(passwords.current, passwords.new);
      toast.success('Contraseña actualizada. Por favor inicia sesión de nuevo.');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al cambiar contraseña';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!regenerateCode || regenerateCode.length !== 6) {
      toast.error('Ingresa un código de 6 dígitos');
      return;
    }

    setRegenerateLoading(true);
    try {
      const response = await authApi.regenerateBackupCodes(regenerateCode);
      setNewBackupCodes(response.data.backup_codes);
      setRegenerateCode('');
      load2faStatus();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Código inválido';
      toast.error(message);
    } finally {
      setRegenerateLoading(false);
    }
  };

  const handleCopyBackupCodes = () => {
    if (!newBackupCodes) return;
    navigator.clipboard.writeText(newBackupCodes.join('\n'));
    setCopiedCodes(true);
    toast.success('Códigos copiados');
    setTimeout(() => setCopiedCodes(false), 3000);
  };

  return (
    <div>
      <Header title="Configuración" subtitle="Gestiona tu cuenta y preferencias" />

      <div className="p-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar tabs */}
          <div className="lg:w-64 flex-shrink-0">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-vida-50 text-vida-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1">
            {/* Profile tab */}
            {activeTab === 'profile' && (
              <div className="card">
                <div className="card-header">
                  <h2 className="text-lg font-semibold">Información del perfil</h2>
                </div>
                <div className="card-body space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 bg-vida-100 rounded-full flex items-center justify-center">
                      <span className="text-vida-600 text-2xl font-bold">
                        {user?.profile?.first_name?.[0] || user?.email?.[0]?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <button className="btn-secondary text-sm">Cambiar foto</button>
                      <p className="text-xs text-gray-500 mt-1">JPG, PNG. Máximo 2MB</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Nombre</label>
                      <input
                        type="text"
                        className="input"
                        defaultValue={user?.profile?.first_name || ''}
                      />
                    </div>
                    <div>
                      <label className="label">Apellido</label>
                      <input
                        type="text"
                        className="input"
                        defaultValue={user?.profile?.last_name || ''}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label">Email</label>
                    <input
                      type="email"
                      className="input bg-gray-50"
                      value={user?.email || ''}
                      disabled
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      El email no se puede cambiar
                    </p>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <button className="btn-primary">Guardar cambios</button>
                  </div>
                </div>
              </div>
            )}

            {/* Security tab */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                {/* 2FA Card */}
                <div className="card">
                  <div className="card-header">
                    <div className="flex items-center gap-2">
                      <ShieldCheckIcon className="w-5 h-5 text-vida-600" />
                      <h2 className="text-lg font-semibold">Autenticación de dos factores (2FA)</h2>
                    </div>
                  </div>
                  <div className="card-body">
                    {twoFactorLoading ? (
                      <div className="flex justify-center py-4">
                        <LoadingSpinner />
                      </div>
                    ) : twoFactorStatus?.enabled ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <ShieldCheckIcon className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-green-800">2FA Activado</p>
                            <p className="text-sm text-green-600">
                              {twoFactorStatus.enabled_at &&
                                `Activado el ${new Date(twoFactorStatus.enabled_at).toLocaleDateString('es-ES')}`}
                              {' · '}
                              {twoFactorStatus.backup_codes_remaining} códigos de respaldo restantes
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-3">
                          <button
                            onClick={() => {
                              setShowRegenerateModal(true);
                              setNewBackupCodes(null);
                              setRegenerateCode('');
                            }}
                            className="btn-secondary text-sm"
                          >
                            Regenerar códigos de respaldo
                          </button>
                          <button
                            onClick={() => setShowDisableModal(true)}
                            className="btn-danger text-sm"
                          >
                            Desactivar 2FA
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <ShieldCheckIcon className="w-5 h-5 text-gray-500" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-700">2FA No activado</p>
                            <p className="text-sm text-gray-500">
                              Añade una capa extra de seguridad a tu cuenta
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowSetupModal(true)}
                          className="btn-primary text-sm"
                        >
                          Activar 2FA
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Password change card */}
                <div className="card">
                  <div className="card-header">
                    <h2 className="text-lg font-semibold">Cambiar contraseña</h2>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                      <div>
                        <label className="label">Contraseña actual</label>
                        <input
                          type="password"
                          className="input"
                          value={passwords.current}
                          onChange={(e) =>
                            setPasswords({ ...passwords, current: e.target.value })
                          }
                          required
                        />
                      </div>
                      <div>
                        <label className="label">Nueva contraseña</label>
                        <input
                          type="password"
                          className="input"
                          value={passwords.new}
                          onChange={(e) =>
                            setPasswords({ ...passwords, new: e.target.value })
                          }
                          required
                          minLength={8}
                        />
                      </div>
                      <div>
                        <label className="label">Confirmar nueva contraseña</label>
                        <input
                          type="password"
                          className="input"
                          value={passwords.confirm}
                          onChange={(e) =>
                            setPasswords({ ...passwords, confirm: e.target.value })
                          }
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        className="btn-primary"
                        disabled={loading}
                      >
                        {loading ? 'Actualizando...' : 'Actualizar contraseña'}
                      </button>
                    </form>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header">
                    <h2 className="text-lg font-semibold">Sesiones activas</h2>
                  </div>
                  <div className="card-body">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <KeyIcon className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Sesión actual</p>
                          <p className="text-sm text-gray-500">Este dispositivo</p>
                        </div>
                      </div>
                      <span className="badge-success">Activa</span>
                    </div>
                    <button className="btn-danger mt-4">
                      Cerrar todas las demás sesiones
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications tab */}
            {activeTab === 'notifications' && (
              <div className="card">
                <div className="card-header">
                  <h2 className="text-lg font-semibold">Preferencias de notificaciones</h2>
                </div>
                <div className="card-body">
                  <NotificationPreferencesEditor />
                </div>
              </div>
            )}

            {/* Appearance tab */}
            {activeTab === 'appearance' && (
              <div className="card">
                <div className="card-header">
                  <h2 className="text-lg font-semibold">Apariencia</h2>
                </div>
                <div className="card-body space-y-6">
                  <div>
                    <label className="label">Tema</label>
                    <div className="grid grid-cols-3 gap-4 mt-2">
                      {[
                        { id: 'light', label: 'Claro', color: 'bg-white border-2' },
                        { id: 'dark', label: 'Oscuro', color: 'bg-gray-900' },
                        { id: 'system', label: 'Sistema', color: 'bg-gradient-to-r from-white to-gray-900' },
                      ].map((theme) => (
                        <button
                          key={theme.id}
                          className={`p-4 rounded-lg border-2 transition-colors ${
                            theme.id === 'light' ? 'border-vida-500' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className={`w-full h-12 rounded ${theme.color} border border-gray-200`} />
                          <p className="text-sm font-medium mt-2">{theme.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="label">Idioma</label>
                    <select className="input max-w-xs">
                      <option value="es">Español</option>
                      <option value="en">English</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 2FA Setup Modal */}
      <Modal
        open={showSetupModal}
        onClose={() => setShowSetupModal(false)}
        title="Configurar 2FA"
        size="lg"
      >
        <TwoFactorSetup
          onComplete={() => {
            setShowSetupModal(false);
            load2faStatus();
          }}
          onCancel={() => setShowSetupModal(false)}
        />
      </Modal>

      {/* 2FA Disable Modal */}
      <Modal
        open={showDisableModal}
        onClose={() => setShowDisableModal(false)}
        title="Desactivar 2FA"
      >
        <TwoFactorDisable
          onComplete={() => {
            setShowDisableModal(false);
            load2faStatus();
          }}
          onCancel={() => setShowDisableModal(false)}
        />
      </Modal>

      {/* Regenerate Backup Codes Modal */}
      <Modal
        open={showRegenerateModal}
        onClose={() => setShowRegenerateModal(false)}
        title="Regenerar códigos de respaldo"
      >
        {newBackupCodes ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800 font-medium">Nuevos códigos generados</p>
              <p className="text-xs text-green-600 mt-1">Los códigos anteriores ya no son válidos.</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {newBackupCodes.map((code, i) => (
                <code key={i} className="text-sm font-mono bg-gray-50 px-3 py-1.5 rounded border text-center">
                  {code}
                </code>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={handleCopyBackupCodes} className="btn-secondary flex-1">
                {copiedCodes ? (
                  <span className="flex items-center justify-center gap-1">
                    <CheckIcon className="w-4 h-4" />
                    Copiados
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-1">
                    <ClipboardDocumentIcon className="w-4 h-4" />
                    Copiar todos
                  </span>
                )}
              </button>
              <button onClick={() => setShowRegenerateModal(false)} className="btn-primary flex-1">
                Listo
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleRegenerateBackupCodes} className="space-y-4">
            <p className="text-sm text-gray-500">
              Ingresa un código TOTP de tu app de autenticación para generar nuevos códigos de respaldo. Los códigos actuales serán reemplazados.
            </p>
            <div>
              <label className="label">Código TOTP</label>
              <input
                type="text"
                className="input text-center text-xl tracking-widest font-mono"
                placeholder="000000"
                value={regenerateCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setRegenerateCode(val);
                }}
                autoFocus
                maxLength={6}
              />
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowRegenerateModal(false)} className="btn-secondary flex-1">
                Cancelar
              </button>
              <button type="submit" className="btn-primary flex-1" disabled={regenerateLoading || regenerateCode.length !== 6}>
                {regenerateLoading ? <LoadingSpinner size="sm" /> : 'Regenerar'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
